import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Pause, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Testimonial {
  id?: string | number;
  name: string;
  title?: string;
  organization?: string;
  quote: string;
  photoUrl?: string;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const TestimonialsSection: React.FC = () => {
  // 暫時隱藏評價入口
  return null;
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchTestimonials = async () => {
      try {
        const res = await apiFetch('/api/public/testimonials');
        if (!res.ok) throw new Error('Failed to fetch testimonials');
        const json = await res.json();
        if (cancelled) return;
        const list: Testimonial[] = Array.isArray(json) ? json : json.data ?? json.testimonials ?? [];
        setTestimonials(list);
      } catch {
        if (cancelled) return;
        setTestimonials([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTestimonials();
    return () => {
      cancelled = true;
    };
  }, []);

  const goTo = useCallback((index: number) => {
    setActiveIndex((prev) => {
      const len = testimonials.length;
      if (len === 0) return prev;
      if (index < 0) return len - 1;
      if (index >= len) return 0;
      return index;
    });
  }, [testimonials.length]);

  useEffect(() => {
    if (!isPlaying || testimonials.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(id);
  }, [isPlaying, testimonials.length]);

  if (loading) {
    return (
      <section className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-stone-100 animate-pulse">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="aspect-square bg-stone-200 rounded-2xl" />
              <div className="md:col-span-2 space-y-4">
                <div className="h-6 bg-stone-200 rounded w-3/4" />
                <div className="h-4 bg-stone-200 rounded w-full" />
                <div className="h-4 bg-stone-200 rounded w-5/6" />
                <div className="h-4 bg-stone-200 rounded w-1/3" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return (
      <section className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1.5 bg-[#FFF7ED] text-[#C2410C] text-sm font-bold rounded-full mb-4">
              權威見證
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
              他們看見的改變
            </h2>
            <p className="text-stone-500">暫時沒有見證，請稍後再來。</p>
          </motion.div>
        </div>
      </section>
    );
  }

  const active = testimonials[activeIndex];

  return (
    <section className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-block px-4 py-1.5 bg-[#FFF7ED] text-[#C2410C] text-sm font-bold rounded-full mb-4">
            權威見證
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
            他們看見的改變
          </h2>
          <p className="text-stone-600 leading-relaxed">
            來自學校、企業與社區伙伴的第一手觀察，見證好餸社企帶來的雙重改變。
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="relative bg-white rounded-2xl md:rounded-3xl p-6 md:p-10 border border-stone-100 shadow-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 items-center">
            <div className="md:col-span-1">
              <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active.photoUrl || activeIndex}
                    src={active.photoUrl}
                    alt={active.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </AnimatePresence>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="w-12 h-12 bg-[#FFF7ED] rounded-full flex items-center justify-center mb-6">
                <Quote className="text-[#C2410C]" size={24} />
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.4 }}
                >
                  <p className="text-lg md:text-2xl text-stone-800 leading-relaxed mb-8 font-medium">
                    「{active.quote}」
                  </p>
                  <div>
                    <p className="text-base md:text-lg font-bold text-[#C2410C]">
                      {active.name}
                    </p>
                    {(active.title || active.organization) && (
                      <p className="text-stone-500 text-sm">
                        {[active.title, active.organization].filter(Boolean).join('，')}
                      </p>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center justify-center md:justify-end gap-3 mt-8">
            <button
              onClick={() => goTo(activeIndex - 1)}
              className="p-2 rounded-full border border-stone-200 text-stone-500 hover:border-[#C2410C] hover:text-[#C2410C] transition-colors"
              aria-label="上一則"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="flex items-center gap-2">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    idx === activeIndex ? 'bg-[#C2410C]' : 'bg-stone-300 hover:bg-stone-400'
                  }`}
                  aria-label={`跳到第 ${idx + 1} 則見證`}
                />
              ))}
            </div>

            <button
              onClick={() => goTo(activeIndex + 1)}
              className="p-2 rounded-full border border-stone-200 text-stone-500 hover:border-[#C2410C] hover:text-[#C2410C] transition-colors"
              aria-label="下一則"
            >
              <ChevronRight size={20} />
            </button>

            <button
              onClick={() => setIsPlaying((p) => !p)}
              className="ml-2 p-2 rounded-full bg-[#FFF7ED] text-[#C2410C] hover:bg-[#ffedd5] transition-colors"
              aria-label={isPlaying ? '暫停輪播' : '播放輪播'}
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} fill="currentColor" />}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
